import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { Plus, Search, Edit3, Trash2, ExternalLink } from "lucide-react";

export default function AdminArticles() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/admin/articles");
    setItems(r.data.articles);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`);
    load();
  };

  const filtered = items.filter((a) =>
    !q || a.title.toLowerCase().includes(q.toLowerCase()) || a.slug.toLowerCase().includes(q.toLowerCase())
  );

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
  };

  return (
    <div data-testid="admin-articles-page">
      <header className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">Content</p>
          <h1 className="font-serif text-3xl text-aubergine">Articles</h1>
          <p className="mt-2 text-sm text-aubergine/65">Write, edit and publish editorial content.</p>
        </div>
        <Link to="/admin/articles/new" data-testid="admin-new-article-button" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2.5 font-mono text-xs tracking-[0.04em]">
          <Plus className="h-3.5 w-3.5" /> New article
        </Link>
      </header>

      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-aubergine/40" />
        <input
          data-testid="admin-articles-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search articles…"
          className="w-full rounded-md border border-aubergine/15 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-champagne"
        />
      </div>

      <div className="rounded-lg border border-aubergine/10 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-articles-table">
          <thead className="bg-bone/60">
            <tr className="text-left text-[11px] font-mono uppercase tracking-[0.12em] text-aubergine/55">
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3 hidden md:table-cell">Category</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 hidden lg:table-cell">Author</th>
              <th className="px-4 py-3 hidden lg:table-cell">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-aubergine/55">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-12 text-center text-aubergine/55" data-testid="admin-articles-empty">
                No articles yet. <Link to="/admin/articles/new" className="text-champagne hover:underline">Create the first one →</Link>
              </td></tr>
            ) : filtered.map((a) => (
              <tr key={a.article_id} className="border-t border-aubergine/10 hover:bg-bone/40 transition-colors">
                <td className="px-4 py-3">
                  <Link to={`/admin/articles/${a.article_id}`} className="font-medium text-aubergine hover:text-champagne" data-testid={`admin-article-row-${a.article_id}`}>{a.title}</Link>
                  <div className="text-xs text-aubergine/55 truncate max-w-xs">/{a.slug}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell"><span className="font-mono text-[10px] uppercase tracking-wider text-aubergine/65">{a.category}</span></td>
                <td className="px-4 py-3">
                  {a.status === "published" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-champagne/15 text-aubergine px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider"><span className="h-1.5 w-1.5 rounded-full bg-champagne" /> Published</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-aubergine/10 text-aubergine/65 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider"><span className="h-1.5 w-1.5 rounded-full bg-aubergine/40" /> Draft</span>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-aubergine/65">{a.author_name || a.author_id?.slice(0, 8) || "—"}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-aubergine/65">{formatDate(a.updated_at)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    {a.status === "published" && (
                      <a href={`/articles/${a.slug}`} target="_blank" rel="noreferrer" data-testid={`admin-article-view-${a.article_id}`} className="text-aubergine/55 hover:text-champagne" title="View public"><ExternalLink className="h-3.5 w-3.5" /></a>
                    )}
                    <Link to={`/admin/articles/${a.article_id}`} data-testid={`admin-article-edit-${a.article_id}`} className="text-aubergine/55 hover:text-champagne" title="Edit"><Edit3 className="h-3.5 w-3.5" /></Link>
                    <button onClick={() => onDelete(a.article_id)} data-testid={`admin-article-delete-${a.article_id}`} className="text-aubergine/55 hover:text-signal-red" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
